// utils/previewCache.js
// Lightweight IndexedDB helper for storing large Base-64 preview images so
// localStorage drafts stay well below quota.
// ------------------------------------------------------------
// API:
//   await savePreview(base64Data) -> id (string)
//   await loadPreview(id)        -> base64Data (string or '')
//   await deletePreview(id)      -> void
//
// If IndexedDB is unavailable (very old browser / strict private mode)
// every call gracefully resolves to "" / the passed-through behaviour so
// that the rest of the app continues working.

const DB_NAME = 'kissandra-cache';
const STORE_NAME = 'previews';
const DB_VERSION = 1;

function openDB() {
  return new Promise((resolve, reject) => {
    if (!('indexedDB' in window)) {
      console.warn('[PreviewCache] IndexedDB not supported');
      return resolve(null);
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'id' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function sha1(str) {
  const buf = await crypto.subtle.digest('SHA-1', new TextEncoder().encode(str));
  return Array.from(new Uint8Array(buf)).map(b => b.toString(16).padStart(2, '0')).join('');
}

export async function savePreview(data) {
  if (!data || !data.startsWith('data:')) return '';
  const db = await openDB();
  if (!db) return '';
  const id = (await sha1(data)).slice(0, 20);
  return new Promise((resolve) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    // Only add if not present
    const getReq = store.get(id);
    getReq.onsuccess = () => {
      if (!getReq.result) {
        store.put({ id, data, ts: Date.now() });
      }
      resolve(id);
    };
    getReq.onerror = () => {
      console.warn('[PreviewCache] get failed', getReq.error);
      resolve(id);
    };
  });
}

export async function loadPreview(id) {
  if (!id) return '';
  const db = await openDB();
  if (!db) return '';
  return new Promise((resolve) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const req = store.get(id);
    req.onsuccess = () => resolve(req.result?.data || '');
    req.onerror = () => {
      console.warn('[PreviewCache] load failed', req.error);
      resolve('');
    };
  });
}

export async function deletePreview(id) {
  if (!id) return;
  const db = await openDB();
  if (!db) return;
  return new Promise((resolve) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).delete(id).onsuccess = () => resolve();
    tx.onerror = () => resolve();
  });
}

// Helper function to clean up old preview before storing new one
export async function cleanupAndSavePreview(data, oldPreviewRef = null) {
  if (!data || !data.startsWith('data:')) return '';
  
  // Clean up old preview if it exists
  if (oldPreviewRef && oldPreviewRef.startsWith('db:')) {
    const oldId = oldPreviewRef.slice(3);
    console.log('[PreviewCache] Cleaning up old preview:', oldId);
    await deletePreview(oldId);
  }
  
  // Save new preview
  return await savePreview(data);
}

// Utility function to get all stored preview IDs (for debugging/cleanup)
export async function getAllPreviewIds() {
  const db = await openDB();
  if (!db) return [];
  
  return new Promise((resolve) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const req = store.getAllKeys();
    req.onsuccess = () => resolve(req.result || []);
    req.onerror = () => {
      console.warn('[PreviewCache] getAllPreviewIds failed', req.error);
      resolve([]);
    };
  });
}

// Utility function to get database size info (for debugging)
export async function getStorageInfo() {
  const db = await openDB();
  if (!db) return { count: 0, estimatedSize: 0 };
  
  return new Promise((resolve) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const req = store.getAll();
    req.onsuccess = () => {
      const results = req.result || [];
      const estimatedSize = results.reduce((total, item) => {
        return total + (item.data ? item.data.length : 0);
      }, 0);
      resolve({ 
        count: results.length, 
        estimatedSize,
        estimatedSizeMB: Math.round(estimatedSize / (1024 * 1024) * 100) / 100
      });
    };
    req.onerror = () => {
      console.warn('[PreviewCache] getStorageInfo failed', req.error);
      resolve({ count: 0, estimatedSize: 0 });
    };
  });
}
